import { HblViewTabs } from '@/types/hbl/hbl.enums';
import { StatusOption, TabItem } from '@/types/common';
import { PermissionsHBLFunctions } from '@/constants/permissions';
import { GeneralVolumeUnits, GeneralWeightUnits } from '@/constants/measurementUnits';
import { HblEntity, HblPalletsForm } from '@/types/hbl/hbl.entity';
import { storeToRefs } from 'pinia';
import { useUnitsStore } from '@/store/modules/useUnitsStore';
import { useLayout } from '@/composables/useLayout';

export const HBL_VIEW_TABS_OPTIONS: TabItem[] = [
  { text: 'HblDetails', value: HblViewTabs.Details },
  {
    text: 'Milestones',
    value: HblViewTabs.Milestones,
    permissionKey: PermissionsHBLFunctions.HBL_MILESTONES,
  },
  {
    text: 'Charges',
    value: HblViewTabs.Charges,
    permissionKey: [
      PermissionsHBLFunctions.HBL_AUTO_CHARGES,
      PermissionsHBLFunctions.HBL_MANUAL_CHARGES,
    ],
  },
  {
    text: 'Attachments',
    value: HblViewTabs.Attachments,
    permissionKey: PermissionsHBLFunctions.HBL_ATTACHMENTS,
  },
  {
    text: 'In Bond',
    value: HblViewTabs.ItApplication,
    permissionKey: PermissionsHBLFunctions.HBL_IT,
  },
  { text: 'Print', value: HblViewTabs.Print, permissionKey: PermissionsHBLFunctions.HBL_PRINT },
];

export const HBL_VIEW_TABS = {
  details: 'DETAILS',
  milestones: 'MILESTONES',
  charges: 'CHARGES',
  attachments: 'ATTACHMENTS',
  photos: 'PHOTOS',
  itApplication: 'IT_APPLICATION',
  print: 'PRINT',
};

export enum HblStatus {
  DRAFT = 'Draft',
  ARRIVED = 'Arrived/Closed',
  RELEASED = 'Released',
  DELIVERED = 'Delivered',
  AVAILABLE = 'In CFS',
  PICKED_UP = 'Picked Up/Closed',
  CUSTOMS_CLEARED = 'Customs Cleared',
  IN_TRANSIT = 'In Transit',
  IN_INVENTORY = 'In inventory',
  IN_DETENTION = 'In Detention',
  IN_LOAD_GUIDE = 'In load guide',
  IN_LOAD_PLAN = 'In load plan',
  IN_GO = 'IN GO',
  SOFT_DELETED = 'Soft Deleted',
  READY_FOR_PICKUP = 'Ready For Pickup',
}

export const HblStatusOptions: Readonly<Record<HblStatus, StatusOption>> = {
  [HblStatus.ARRIVED]: {
    title: HblStatus.ARRIVED,
    color: '#FFECE8',
  },
  [HblStatus.CUSTOMS_CLEARED]: {
    title: HblStatus.CUSTOMS_CLEARED,
    color: '#FFECE8',
  },
  [HblStatus.IN_GO]: {
    title: HblStatus.IN_GO,
    color: '#FFECE8',
  },
  [HblStatus.RELEASED]: {
    title: HblStatus.RELEASED,
    color: '#FFECE8',
  },
  [HblStatus.AVAILABLE]: {
    title: HblStatus.AVAILABLE,
    color: '#FFECE8',
  },
  [HblStatus.IN_TRANSIT]: {
    title: HblStatus.IN_TRANSIT,
    color: '#FFECE8',
  },
  [HblStatus.IN_INVENTORY]: {
    title: HblStatus.IN_INVENTORY,
    color: '#FFECE8',
  },
  [HblStatus.IN_LOAD_GUIDE]: {
    title: HblStatus.IN_LOAD_GUIDE,
    color: '#FFECE8',
  },
  [HblStatus.IN_DETENTION]: {
    title: HblStatus.IN_DETENTION,
    textColor: '!text-white',
    color: '#f6745b',
  },
  [HblStatus.IN_LOAD_PLAN]: {
    title: HblStatus.IN_LOAD_PLAN,
    color: '#FFECE8',
  },
  [HblStatus.DELIVERED]: {
    title: HblStatus.DELIVERED,
    color: '#196A35',
    textColor: '!text-white',
  },
  [HblStatus.PICKED_UP]: {
    title: HblStatus.PICKED_UP,
    color: '#FFECE8',
  },
  [HblStatus.SOFT_DELETED]: {
    title: 'Soft Deleted',
    color: '#CA24249C',
    textColor: '!text-white',
  },
  [HblStatus.READY_FOR_PICKUP]: {
    title: HblStatus.READY_FOR_PICKUP,
    color: '#196A35',
    textColor: '!text-white',
  },
  [HblStatus.DRAFT]: { title: 'Draft', color: '' },
};

export const HBL_DEFAULT_FILTERS = () => {
  return {
    pageNumber: 1,
    itemsPerPage: 10,
    sortBy: {
      name: 'createdAt',
      order: -1,
    },
  };
};

export const HBL_FORM_STEPS = [
  {
    label: 'HBLReference',
    isValid: true,
    permissionKey: PermissionsHBLFunctions.HBL_REFERENCE,
    component: () => import('@/components/house-bl/forms/HBLReferenceFormComponent.vue'),
  },
  {
    label: 'CargoDetails',
    isValid: true,
    permissionKey: PermissionsHBLFunctions.HBL_CARGO_DETAILS,
    component: () => import('@/components/house-bl/forms/CargoDetailsFormWrapper.vue'),
  },
  {
    label: 'Release',
    isValid: true,
    permissionKey: PermissionsHBLFunctions.HBL_RELEASE,
    component: () => import('@/components/house-bl/forms/ReleaseFormComponent.vue'),
  },
  {
    label: 'Notes',
    isValid: true,
    permissionKey: PermissionsHBLFunctions.HBL_NOTES,
    component: () =>
      import('@/components/master-bl/forms/components/AdditionalNotesFormComponent.vue'),
  },
  {
    label: 'HeadLoadDelivery',
    isValid: true,
    permissionKey: PermissionsHBLFunctions.HBL_HEAD_LOAD_DELIVERY,
    component: () => import('@/components/house-bl/forms/HeadLoadDeliveryFormComponent.vue'),
  },
];

export const HBL_FORM_DEFAULT_DATA = (defaultValues?: Partial<HblEntity>) => {
  const { importWeightUnit, importVolumeUnit } = storeToRefs(useUnitsStore());

  return {
    paymentLookup: null,
    mblNumber: '',
    mblId: '',
    amsHblNumber: '',
    hblNumber: '',
    goNumber: '',
    shipper: '',
    consignee: '',
    coLoader: null,
    customerRef: '',
    manifestedPcs: 0,
    manifestedPackageType: null,
    manifestedPallets: 0,
    emergencyContactNumber: '',
    receivedPcs: 0,
    packageType: '',
    entryType: null,
    receivedPackageType: null,
    receivedPallets: 0,
    outPcs: 0,
    outPackageType: null,
    isIpi: false,
    isAutoItNumber: false,
    itRequired: false,
    manifestedWeight: 0,
    manifestedWeightUnit: importWeightUnit.value,
    manifestedVolume: 0,
    manifestedVolumeUnit: importVolumeUnit.value,
    receivedWeight: 0,
    receivedWeightUnit: importWeightUnit.value,
    receivedVolume: 0,
    receivedVolumeUnit: importVolumeUnit.value,
    exchangePallets: 0,
    receivedExchangePallets: 0,
    warehouseLocation: '',
    headLoad: false,
    doorDelivery: false,
    marks: '',
    description: '',
    cargoHold: false,
    cargoHoldNotes: '',
    markHold: false,
    markHoldNotes: '',
    hazardous: false,
    hazDetails: null,
    unNumber: '',
    chemicalName: '',
    chemicalClass: '',
    packingGroup: null,
    broker: '',
    itNumber: '',
    itDate: '',
    itApprovalDate: '',
    entryNumber: '',
    entryDate: '',
    isMarkReleased: false,
    customReleasedBy: '',
    goDate: '',
    customReleasedDate: '',
    customReleased: false,
    domesticMove: false,
    isExchangePalletResolved: false,
    frieghtReleased: false,
    releaseTo: '',
    estPickupDate: '',
    releasedBy: '',
    driver: '',
    driverLicense: '',
    proNumber: '',
    readyForPickupDate: '',
    deliveryReleasedDate: '',
    releasedNotes: '',
    loadDate: '',
    etd: '',
    eta: '',
    shipDate: '',
    arrivalDate: '',
    pod: null,
    deliverTo: '',
    trackingNumber: '',
    deliverToAddress: '',
    deliveryOrder: false,
    deliverNotes: '',
    exceptionNotes: '',
    hblPics: [],
    internalNotes: '',
    externalNotes: '',
    viewedByCustomer: '',
    ipiNotes: '',
    devanNotes: '',
    accountingNotes: '',
    eGuarenteeBy: '',
    truckerName: '',
    address: '',
    depositAmount: '',
    containerReturnDate: '',
    containerPickUpDate: '',
    lfd: '',
    availableDate: null,
    warehouse: null,
    inventoryLocation: null,
    pallets: [PALLET_DEFAULT_DATA()],
    warehouseLoc: null,
    actualPickupDate: null,
    deliveredDate: null,
    consigneeAddress: '',
    ipiEventNotes: [],

    lfdTimeZone: '',
    etdTimeZone: '',
    etaTimeZone: '',
    itDateTimeZone: '',
    goDateTimeZone: '',
    shipDateTimeZone: '',
    loadDateTimeZone: '',
    entryDateTimeZone: '',
    arrivalDateTimeZone: '',
    availableDateTimeZone: '',
    deliveredDateTimeZone: '',
    estPickupDateTimeZone: '',
    itApprovalDateTimeZone: '',
    actualPickupDateTimeZone: '',
    customReleasedDateTimeZone: '',
    containerPickUpDateTimeZone: '',
    containerReturnDateTimeZone: '',
    deliveryReleasedDateTimeZone: '',
    readyForPickupDateTimeZone: '',
    nonCommissionable: false,
    nonStackable: false,
    isControlShipment: false,
    exitCity: null,
    exitWarehouse: null,
    isForceRelease: false,
    ...defaultValues,
  };
};

export const HAZMAT_DEFAULT_DATA = (defaultValues?: Partial<HblEntity>) => {
  const { importWeightUnit, importVolumeUnit } = storeToRefs(useUnitsStore());

  return {
    unNumber: '',
    emergencyContactNumber: '',
    chemicalName: '',
    chemicalClass: '',
    packingGroup: null,
    manifestedPcs: 0,
    receivedPcs: 0,
    flashPoints: '',
    manifestedPackageType: null,
    receivedPackageType: null,
    manifestedPallets: 0,
    receivedPallets: 0,
    manifestedWeight: 0,
    manifestedWeightUnit: importWeightUnit.value,
    receivedWeight: 0,
    receivedWeightUnit: importWeightUnit.value,
    manifestedVolume: 0,
    manifestedVolumeUnit: importVolumeUnit.value,
    receivedVolume: 0,
    receivedVolumeUnit: importVolumeUnit.value,
    ...defaultValues,
  };
};

export const PALLET_DEFAULT_DATA = (): HblPalletsForm => ({
  inventoryLocation: '',
  inventoryLocationId: '',
  receivedWeight: 0,
  receivedWeightUnit: GeneralWeightUnits.KGS,
  receivedVolume: 0,
  receivedVolumeUnit: GeneralVolumeUnits.CFT,
  packageLength: 0,
  packageWidth: 0,
  packageHeight: 0,
  receivedPallets: 0,
  nonCommissionable: false,
  nonStackable: false,
});

export const HBL_FORM_DEFAULT_CARDS = () => {
  return [
    {
      text: 'Exception Notes',
      value: 'exceptionNotes',
      permissionKey: PermissionsHBLFunctions.HBL_EXCEPTION_NOTES,
    },
    {
      text: 'Internal Notes',
      value: 'internalNotes',
      permissionKey: PermissionsHBLFunctions.HBL_INTERNAL_NOTES,
    },
    {
      text: 'External Notes',
      value: 'externalNotes',
      permissionKey: PermissionsHBLFunctions.HBL_EXTERNAL_NOTES,
    },
    {
      text: 'Ipi Notes',
      value: 'ipiNotes',
      permissionKey: PermissionsHBLFunctions.HBL_IPI_NOTES,
    },
    {
      text: 'Ipi Event Notes',
      value: 'ipiEventNotes',
      permissionKey: PermissionsHBLFunctions.HBL_IPI_EVENT_NOTES,
    },
    {
      text: 'Accounting Notes',
      value: 'accountingNotes',
      permissionKey: PermissionsHBLFunctions.HBL_ACCOUNTING_NOTES,
    },
    {
      text: 'E-Guarantee By',
      value: 'eGuaranteeBy',
      permissionKey: PermissionsHBLFunctions.HBL_E_GUARANTEE_BY,
    },
    {
      text: 'For Customer Eyes Only',
      value: 'forCustomerEyesOnly',
      permissionKey: PermissionsHBLFunctions.HBL_EXTERNAL_NOTES, // same key as external notes
    },
    {
      text: 'Devan Notes',
      value: 'devanNotes',
      permissionKey: PermissionsHBLFunctions.HBL_DEVAN_NOTES,
    },
  ];
};

export const HBL_VIEW_TABS_TABLET_OPTIONS: TabItem[] = [
  {
    text: 'Details',
    value: HblViewTabs.Details,
  },
  {
    text: 'Photos',
    value: HblViewTabs.Photos,
  },
];

export const ENTRY_TYPES = {
  type63: '63',
  type62: '62',
  type61: '61',
};
